import { FormlyExtension, FormlyFieldConfig } from '@ngx-formly/core';
import { LAYOUT_EDIT_NODE_WRAPPER } from './layout-edit-node-wrapper/layout-edit.constants';

type LayoutEditHost = {
    layoutEditMode?: () => boolean;
    isLayoutTreeLocked?: () => boolean;
    fields?: FormlyFieldConfig[];
};

/**
 * FormlyForm's canvas field: `{ type: 'formly-group' }` with no parent, key, or layoutNodeId.
 * Not an addressable layout node — wrapping it shows a false empty-group on nested forms.
 */
export function isFormlySyntheticRoot(field: FormlyFieldConfig): boolean {
    const node = field as FormlyFieldConfig & { layoutNodeId?: string };
    if (field.parent || field.key != null || node.layoutNodeId) {
        return false;
    }
    return field.type == null || field.type === 'formly-group';
}

function formlyTreeRoot(field: FormlyFieldConfig): FormlyFieldConfig {
    let current = field;
    while (current.parent) {
        current = current.parent;
    }
    return current;
}

/**
 * Nested `formly-form`s (postal-address widgets, line-item hosts) share `options` with the
 * covered BaseDetail, but their tree is not `host.fields`. v1 chrome stays on that host tree.
 */
export function isFieldOnHostLayoutTree(
    field: FormlyFieldConfig,
    hostFields: FormlyFieldConfig[] | undefined,
): boolean {
    if (!Array.isArray(hostFields)) {
        return false;
    }
    return formlyTreeRoot(field).fieldGroup === hostFields;
}

function layoutEditHost(field: FormlyFieldConfig): LayoutEditHost | undefined {
    let current: FormlyFieldConfig | undefined = field;
    while (current) {
        const detail = current.options?.formState?.detailComponent as LayoutEditHost | undefined;
        if (detail) {
            return detail;
        }
        current = current.parent;
    }
    return undefined;
}

/**
 * Formly renders the first wrapper as the outermost. Put layout-edit outside
 * `simple-title` so the selection outline includes the field label.
 */
export function prependLayoutEditNodeWrapper(field: FormlyFieldConfig): void {
    const wrappers = field.wrappers ?? [];
    const rest = wrappers.filter((name) => name !== LAYOUT_EDIT_NODE_WRAPPER);
    field.wrappers = [LAYOUT_EDIT_NODE_WRAPPER, ...rest];
}

/**
 * Wraps every addressable node while **layout-edit mode** is on, including a locked **base form**.
 * Runs in {@link postPopulate} so type-level wrappers (e.g. `simple-title`) are already merged.
 */
export class LayoutEditFormlyExtension implements FormlyExtension {
    postPopulate(field: FormlyFieldConfig): void {
        if (isFormlySyntheticRoot(field)) {
            return;
        }
        const detail = layoutEditHost(field);
        if (!detail?.layoutEditMode?.()) {
            return;
        }
        if (!isFieldOnHostLayoutTree(field, detail.fields)) {
            return;
        }
        prependLayoutEditNodeWrapper(field);
    }
}

export function layoutEditExtension() {
    return {
        extensions: [{
            name: 'layoutEdit',
            extension: new LayoutEditFormlyExtension(),
        }],
    };
}
